import { QueryParams } from '../app/types/query-params';
import {
  generateFundamentalParams,
  generateProductCodesUri,
} from './buildRequest';
import {
  dateTimeDateToExcelDate,
  doRequest,
  getNumberFormatFromResolution,
  getWorksheetFromRange,
  setNumberFormat,
} from './util';
import { getDateTime } from './dateHelper';
import { DateTime } from 'luxon';

/* global window, console, Excel, CustomFunctions, Promise */

export async function loadFundamentalData(market_area_id: number) {
  let url = window.api_url + '/time_series/list?';
  if (market_area_id != null) {
    url += 'market_area_id=' + market_area_id;
  }
  const response = await doRequest(url);
  if (response.ok) {
    let obj = await response.json();
    url = window.api_url + '/time_series/data?';
    let time_series = obj.time_series;
    let time_series_length = time_series.length;
    for (let i = 0; i < time_series_length; i++) {
      if (i > 0) {
        url += '&';
      }
      url += 'ids=' + time_series[i].id;
    }
    const detailed_response = await doRequest(url);
    if (detailed_response.ok) {
      let detailed_time_series = await detailed_response.json();
      let ret: string[][] = [];
      let detailed_time_series_length = detailed_time_series.time_series.length;
      for (let i = 0; i < detailed_time_series_length; i++) {
        let keys = Object.keys(detailed_time_series.time_series[i].details);
        let key_count = keys.length;
        if (i === 0) {
          ret.push(['id']);
          for (let j = 0; j < key_count; j++) {
            if (keys[j] !== 'id') {
              ret.push([keys[j]]);
            }
          }
        }
        try {
          for (let j = 0; j < key_count; j++) {
            if (j === 0) {
              ret[j].push(detailed_time_series.time_series[i].details['id']);
            }
            ret[j + 1].push(
              JSON.stringify(
                detailed_time_series.time_series[i].details[keys[j]]
              )
            );
          }
        } catch (error: any) {
          console.error(error);
          return [[error.message]];
        }
      }
      return ret;
    } else {
      return [[]];
    }
  } else {
    let error = await response.json();
    return [[response.statusText + ':' + error.message + '  ' + url]];
  }
}

export async function loadFundamentalDataDetailed(
  product_codes: string[][],
  start_date: string | number,
  end_date: string | number,
  detailed: boolean,
  queryParams: QueryParams,
  outputFormat: string,
  invocation: CustomFunctions.Invocation
) {
  const start = getDateTime(start_date);
  const end = getDateTime(end_date);
  try {
    let url = window.api_url + '/time_series/data?';
    let products;
    if (product_codes === undefined || product_codes.length === 0) {
      return [['EMSA ids must be given']];
    } else {
      products = ([] as any[]).concat.apply([], product_codes);
    }
    let url_product_codes = generateProductCodesUri(products);
    let url_params = generateFundamentalParams(queryParams, start, end, false);
    url += url_product_codes + url_params;
    return await doFundamentalRequest(
      url,
      queryParams.nv_default!,
      queryParams.auto_update!,
      detailed,
      invocation.address!,
      queryParams.timezone,
      outputFormat
    );
  } catch (error: any) {
    console.error(error);
    return [['error occured in addin: ' + error.message]];
  }
}

async function doFundamentalRequest(
  url: string,
  nv_default: string,
  auto_update: boolean,
  detailed: boolean,
  address: string,
  time_zone?: string,
  outputFormat?: string
) {
  const response = await doRequest(url);
  if (!response.ok) {
    let error = await response.json();
    return [[response.statusText + ':' + error.message + '  ' + url]];
  }
  const detailed_time_series = await response.json();
  const allTimezonesEqual = detailed_time_series.time_series.every(
    (time_series) =>
      time_series.details.time_zone ===
      detailed_time_series.time_series[0].details.time_zone
  );
  if (
    !allTimezonesEqual &&
    (time_zone === undefined || time_zone === null || time_zone === '')
  ) {
    return [
      [
        'If you request fundamental data with different timezones, you have to specify the timezone parameter',
      ],
    ];
  }
  let ret: any[][] = [];
  let keys: string[] = [];
  if (outputFormat === 'diagram') {
    keys.push('name');
  } else {
    if (detailed) {
      keys = Object.keys(detailed_time_series.time_series[0].details);
      keys.splice(keys.indexOf('id'), 1);
    } else {
      keys.push('name');
      keys.push('description');
      keys.push('unit');
      keys.push('time_zone');
    }
    keys.unshift('id');
  }
  let key_count = keys.length;
  for (let i = 0; i < key_count; i++) {
    ret.push([
      keys[i] === 'time_zone'
        ? 'Timezone'
        : keys[i] === 'name' && outputFormat === 'diagram'
          ? ''
          : keys[i],
    ]);
  }
  let values_length = detailed_time_series.time_series[0].values.length;
  for (let i = 0; i < values_length; i++) {
    ret.push([
      +dateTimeDateToExcelDate(
        DateTime.fromISO(
          detailed_time_series.time_series[0].values[i][0].split('+')[0]
        )
      ),
    ]);
  }
  const details = detailed_time_series.time_series[0].details;
  let numberFormat =
    details.output_resolution !== null
      ? getNumberFormatFromResolution(details.output_resolution)
      : getNumberFormatFromResolution(details.resolution);
  await setNumberFormat(
    numberFormat,
    detailed_time_series.time_series[0].values.length - 1,
    detailed_time_series.time_series.length,
    key_count,
    0,
    address
  );
  let time_series_length = detailed_time_series.time_series.length;
  let nv_val;
  if (nv_default === undefined || nv_default.toLowerCase() === 'nv') {
    nv_val = auto_update
      ? new CustomFunctions.Error(CustomFunctions.ErrorCode.notAvailable)
      : '=na()';
  } else if (nv_default === '') {
    nv_val = '';
  } else if (nv_default === 'n/a') {
    nv_val = 'N/A';
  }
  for (let i = 0; i < time_series_length; i++) {
    for (let j = 0; j < key_count; j++) {
      if (keys[j] !== 'meta') {
        const key = keys[j] === 'unit' ? 'output_unit' : keys[j];
        let val =
          detailed_time_series.time_series[i].details[key] === null
            ? ''
            : detailed_time_series.time_series[i].details[key];
        if (key === 'time_zone' && time_zone) {
          val = time_zone;
        }
        ret[j].push(val);
      } else {
        ret[j].push('');
      }
    }
    values_length = detailed_time_series.time_series[i].values.length;
    for (let j = 0; j < values_length; j++) {
      let val =
        detailed_time_series.time_series[i].values[j][
          detailed_time_series.time_series[i].values[j].length - 1
        ];
      val = val === null ? nv_val : val;
      ret[j + keys.length].push(val);
    }
  }
  if (auto_update) {
    return new Promise<number[][]>((resolve) => {
      resolve(ret);
    });
  } else {
    let context = new Excel.RequestContext();
    let range = context.workbook.worksheets
      .getItem(getWorksheetFromRange(address))
      .getRange(address.split('!')[1])
      .getResizedRange(ret.length - 1, ret[0].length - 1);
    range.values = ret;
    range.copyFrom(range, Excel.RangeCopyType.values);
    await context.sync();
    return;
  }
}
